/**
 * FarSideNote + RefusedRuleNotes — two coach-only template notices
 * (viewpoint observability Phase 1, design §7A items 4 and 5).
 *
 * FarSideNote: a checkpoint whose landmarks all sit on the side of the body
 * away from the camera in the coach's demo is measured from the limb that is
 * partly behind the body, and is roughly twice as uncertain as the same
 * checkpoint on the near side. The coach could not see this before; now they
 * can decide whether to re-film or accept it.
 *
 * RefusedRuleNotes: a rule the coach typed that the rule compiler could not
 * turn into a measurable check. Previously the coach saw nothing at all — the
 * rule simply never fired, with no explanation.
 *
 * COACH-ONLY. Both source keys live in the backend's
 * `_WRITER_ONLY_METADATA_KEYS`, so they never reach an athlete's or a public
 * viewer's payload. Neither component is rendered from PublicSharePage
 * (pinned by PublicSharePage.noQuotes.test.tsx's sibling pin).
 *
 * Copy comes from `@/utils/checkpointCoverageCopy`; these components are
 * layout only. The visibility numbers on each note entry are diagnostics and
 * are deliberately NOT rendered — a coach cannot act on them
 * (docs/COACH_FACING_COPY_STANDARD.md §2).
 */
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { FarSideNoteEntry } from '@/services/templateApi';
import type { RefusedRuleDetail } from '@/types/ruleCompiler';
import {
  farSideNoteText,
  refusedRuleDetailText,
  refusedRuleText,
} from '@/utils/checkpointCoverageCopy';

export interface FarSideNoteProps {
  /** `metadata.far_side_note` — null/empty on the common path. */
  entries?: FarSideNoteEntry[] | null;
  className?: string;
}

export function FarSideNote({ entries, className }: FarSideNoteProps) {
  if (!entries || entries.length === 0) return null;

  return (
    <section
      aria-label="Checkpoints on the side away from the camera"
      data-testid="far-side-note"
      className={cn(
        'rounded-lg border border-warning/30 bg-warning/10 px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <ExclamationTriangleIcon
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning"
          aria-hidden="true"
        />
        <ul className="space-y-2">
          {entries.map((entry, index) => (
            <li
              key={entry.template_annotation_id || `${entry.label}-${index}`}
              data-testid="far-side-note-row"
              className="text-sm leading-relaxed text-text-secondary"
            >
              {farSideNoteText(entry.label)}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export interface RefusedRuleNotesProps {
  /** `metadata.refused_rule_texts` — null/empty on the common path. */
  texts?: string[] | null;
  /**
   * `metadata.refused_rule_details` — PREFERRED over `texts` when present
   * (PR-8, audit items `RC-13` / `E2E-9`): same refusals, with the reason
   * beside the coach's own phrase. `texts` stays the fallback for a template
   * finalized before PR-8 — no reshape, no backfill (plan T-11).
   */
  details?: RefusedRuleDetail[] | null;
  className?: string;
}

/**
 * THE one refusal surface (audit item `FE-12`).
 *
 * The same refusal used to render twice on this page in two different wordings:
 * here, and as the `rationale` on a `not_expressible` compile card inside
 * `CalibrationTakesPanel` → `RuleCompilerPanel` → `AnnotationSuggestionPanel`.
 * A coach reading one sentence twice, worded two ways, cannot tell whether that
 * is one problem or two. This list wins — it is fed by the key the backend
 * PERSISTS, so it survives a reload and a recompile — and the compile card for
 * a refusal-only status is no longer rendered (see `REFUSAL_ONLY_STATUSES` in
 * `AnnotationSuggestionPanel`).
 */
export function RefusedRuleNotes({ texts, details, className }: RefusedRuleNotesProps) {
  // Prefer the detailed key; fall back to the flat list. Never merge the two:
  // a backend that writes both writes the SAME refusals to each, and rendering
  // both would put every refusal on screen twice — the very duplication this
  // component was consolidated to remove.
  const rows: { key: string; text: string }[] =
    details && details.length > 0
      ? details.map((detail, index) => ({
          key: detail.text || `detail-${index}`,
          text: refusedRuleDetailText(detail.text, detail.reason),
        }))
      : (texts ?? []).map((text, index) => ({
          key: text || `text-${index}`,
          text: refusedRuleText(text),
        }));

  if (rows.length === 0) return null;

  return (
    <section
      aria-label="Rules we could not turn into checks"
      data-testid="refused-rule-notes"
      className={cn(
        'rounded-lg border border-border-subtle bg-dark-elevated-1/70 px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <QuestionMarkCircleIcon
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-muted"
          aria-hidden="true"
        />
        <ul className="space-y-2">
          {rows.map((row, index) => (
            <li
              key={`${row.key}-${index}`}
              data-testid="refused-rule-note-row"
              className="text-sm leading-relaxed text-text-secondary"
            >
              {row.text}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default FarSideNote;
